import { useState } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from 'cpool-components/general/Tooltip';
import { TooltipAlign, TooltipPosition } from 'cpool-components/general/Tooltip/types';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { AGGREGATE_NAMES } from 'cpool-constants';
import type { SchemaUserResolver_GetAclPermissions } from 'cpool-tables/backend-resolvers';
import { getAddedAdvisorId, hasMultipleAssignedUsers } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { LuUserRoundPlus, LuUserRoundX } from 'react-icons/lu';

import { AddAdvisorServiceDialog } from './AddAdvisorServiceDialog';
import { EndAdvisorServiceDialog } from './EndAdvisorServiceDialog';
import type { AdvisorServiceLead } from './types';
import useUserInfo from '~/hooks/useUserInfo';

type Props = {
    labeled?: boolean;
    lead: AdvisorServiceLead;
    permissions: SchemaUserResolver_GetAclPermissions;
};

export const AdvisorServiceButton: React.FC<Props> = ({ labeled = false, lead, permissions }) => {
    const { t } = useTranslation();
    const user = useUserInfo();
    const [addOpen, setAddOpen] = useState(false);
    const [endOpen, setEndOpen] = useState(false);

    const advisorId = getAddedAdvisorId(lead);
    const commands = permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST];
    const canAdd =
        !advisorId && lead.type !== null && !hasMultipleAssignedUsers(lead.type) && commands.addAssignedAdvisor;
    const canEnd = !!advisorId && commands.removeAssignedUser;

    if (!canAdd && !canEnd) return null;

    return (
        <div
            onClick={(evt) => {
                evt.preventDefault();
                evt.stopPropagation();
            }}
        >
            {labeled ? (
                <Button
                    className="w-full"
                    data-testid="advisor-service-button"
                    iconLeft={advisorId ? <LuUserRoundX className="size-4" /> : <LuUserRoundPlus className="size-4" />}
                    onClick={() => (advisorId ? setEndOpen(true) : setAddOpen(true))}
                    variant={ButtonVariant.SECONDARY}
                >
                    {t(advisorId ? 'LEADS.ADVISOR_SERVICE.END_TITLE' : 'LEADS.ADVISOR_SERVICE.ADD_BUTTON')}
                </Button>
            ) : (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span>
                            <Button
                                variant={ButtonVariant.SECONDARY}
                                data-testid="advisor-service-button"
                                aria-label={t(
                                    advisorId
                                        ? 'LEADS.ADVISOR_SERVICE.END_TOOLTIP'
                                        : 'LEADS.ADVISOR_SERVICE.ADD_TOOLTIP',
                                )}
                                onClick={() => (advisorId ? setEndOpen(true) : setAddOpen(true))}
                                iconLeft={
                                    advisorId ? (
                                        <LuUserRoundX className="text-md" />
                                    ) : (
                                        <LuUserRoundPlus className="text-md" />
                                    )
                                }
                            />
                        </span>
                    </TooltipTrigger>
                    <TooltipContent align={TooltipAlign.END} side={TooltipPosition.TOP}>
                        {t(advisorId ? 'LEADS.ADVISOR_SERVICE.END_TOOLTIP' : 'LEADS.ADVISOR_SERVICE.ADD_TOOLTIP')}
                    </TooltipContent>
                </Tooltip>
            )}
            {canAdd && (
                <AddAdvisorServiceDialog loanRequestId={lead.id} open={addOpen} onClose={() => setAddOpen(false)} />
            )}
            {canEnd && advisorId && (
                <EndAdvisorServiceDialog
                    advisorId={advisorId}
                    isOwnShare={advisorId === user?.id}
                    loanRequestId={lead.id}
                    open={endOpen}
                    onClose={() => setEndOpen(false)}
                />
            )}
        </div>
    );
};
